(() => {
  'use strict';

  const config = window.HPDTheme || {};
  const routes = config.routes || {};
  let repairBusy = false;

  function jsonRoute(route, fallback) {
    const value = route || fallback;
    return value.endsWith('.js') ? value : `${value}.js`;
  }

  function kitId(item) {
    return String(item?.properties?._hpd_kit_id || '');
  }

  function kitRole(item) {
    return String(item?.properties?._hpd_kit_role || '');
  }

  function kitMode(item) {
    return String(item?.properties?._hpd_kit_mode || '');
  }

  function kitBrand(item) {
    return String(item?.properties?._hpd_kit_brand || '');
  }

  function requiredLevel(group) {
    const toolQuantity = group.reduce((total, item) => (
      kitRole(item) === 'tool' ? total + Number(item.quantity || 0) : total
    ), 0);
    const mode = group.map(kitMode).find(Boolean);
    const toolBrands = new Set(group.filter((item) => kitRole(item) === 'tool' && Number(item.quantity || 0) > 0).map(kitBrand));
    const setBrands = new Set(group.filter((item) => kitRole(item) === 'set' && Number(item.quantity || 0) > 0).map(kitBrand));

    if (!mode) {
      const hasPowerSet = group.some((item) => kitRole(item) === 'set' && Number(item.quantity || 0) > 0);
      if (!hasPowerSet) return 0;
    } else if (mode === 'both') {
      if (!toolBrands.has('hikoki') || !toolBrands.has('metabo') || !setBrands.has('hikoki') || !setBrands.has('metabo')) return 0;
    } else if (!toolBrands.has(mode) || !setBrands.has(mode)) {
      return 0;
    }
    if (toolQuantity >= 4) return 10;
    if (toolQuantity >= 2) return 5;
    return 0;
  }

  function cleanedProperties(properties = {}) {
    return Object.fromEntries(Object.entries(properties).filter(([key]) => ![
      'Kit saving',
      '_hpd_kit_id',
      '_hpd_kit_role',
      '_hpd_kit_mode',
      '_hpd_kit_brand',
      '_hpd_kit_discount_level',
      '_hpd_kit_expired',
    ].includes(key)));
  }

  function targetProperties(item, level) {
    const remaining = cleanedProperties(item.properties);
    if (!level) return { ...remaining, _hpd_kit_expired: 'true' };
    return {
      ...remaining,
      'Kit saving': `${level}%`,
      _hpd_kit_id: kitId(item),
      _hpd_kit_role: kitRole(item),
      ...(kitMode(item) ? { _hpd_kit_mode: kitMode(item) } : {}),
      ...(kitBrand(item) ? { _hpd_kit_brand: kitBrand(item) } : {}),
      _hpd_kit_discount_level: String(level),
    };
  }

  function needsUpdate(item, level) {
    if (!level) return Boolean(kitId(item));
    return String(item?.properties?.['Kit saving'] || '') !== `${level}%`
      || String(item?.properties?._hpd_kit_discount_level || '') !== String(level);
  }

  function kitGroups(cart) {
    const groups = new Map();
    (Array.isArray(cart?.items) ? cart.items : []).forEach((item) => {
      const id = kitId(item);
      if (!id) return;
      if (!groups.has(id)) groups.set(id, []);
      groups.get(id).push(item);
    });
    return groups;
  }

  async function changeLine(item, properties) {
    const response = await fetch(jsonRoute(routes.cartChange, '/cart/change'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: item.key,
        quantity: item.quantity,
        properties,
      }),
    });
    if (!response.ok) throw new Error('Unable to refresh the custom-kit saving.');
    return response.json();
  }

  async function repairKitSavings(cart) {
    if (repairBusy || !Array.isArray(cart?.items)) return cart;
    if (!kitGroups(cart).size) return cart;

    repairBusy = true;
    let latestCart = cart;
    let changed = false;
    try {
      let pending = true;
      let safety = 0;
      while (pending && safety < 80) {
        pending = false;
        safety += 1;
        const groups = kitGroups(latestCart);
        for (const [, group] of groups) {
          const level = requiredLevel(group);
          const item = group.find((line) => needsUpdate(line, level));
          if (!item) continue;
          latestCart = await changeLine(item, targetProperties(item, level));
          changed = true;
          pending = true;
          break;
        }
      }
    } catch (error) {
      // Cart housekeeping must never prevent the shopper from using the cart.
    } finally {
      repairBusy = false;
    }

    if (changed) {
      window.dispatchEvent(new CustomEvent('cartcount:update', { detail: latestCart }));
    }
    return latestCart;
  }

  async function fetchCartAndRepair() {
    try {
      const response = await fetch(jsonRoute(routes.cart, '/cart'), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;
      await repairKitSavings(await response.json());
    } catch (error) {
      // Normal cart behaviour remains available if a housekeeping request fails.
    }
  }

  window.addEventListener('cartcount:update', (event) => {
    if (Array.isArray(event.detail?.items)) repairKitSavings(event.detail);
  });

  if (Number(config.initialCartCount || 0) > 0) {
    window.setTimeout(fetchCartAndRepair, 150);
  }
})();
